import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import Icon from '@mdi/react';
import { mdiDownload, mdiChevronDown, mdiChevronUp, mdiLoading, mdiAlertCircleOutline } from '@mdi/js';

type AssetInfo = { sha256: string; url: string | null };
type ReleaseInfo = { ok: boolean; version: string | null; assets: Record<string, AssetInfo> | null };

const AGENT_TYPES: { id: string; label: string }[] = [
    { id: 'mikrotik', label: 'MikroTik' },
    { id: 'opnsense', label: 'OPNsense' },
];

const OS_LABELS: Record<string, string> = {
    linux: 'Linux',
    windows: 'Windows',
};

const ARCH_LABELS: Record<string, string> = {
    amd64: 'x86_64 (amd64)',
    arm64: 'ARM 64-bit (arm64)',
    arm: 'ARM 32-bit v7 (armv7)',
};

const ALL_PLATFORMS: { os: string; arch: string }[] = [
    { os: 'linux', arch: 'amd64' },
    { os: 'linux', arch: 'arm64' },
    { os: 'linux', arch: 'arm' },
    { os: 'windows', arch: 'amd64' },
];

function detectPlatform(): { os: string; arch: string } {
    const ua = navigator.userAgent.toLowerCase();
    const os = ua.includes('windows') ? 'windows' : 'linux';
    let arch = 'amd64';
    if (ua.includes('aarch64') || ua.includes('arm64')) {
        arch = 'arm64';
    } else if (ua.includes('armv7') || ua.includes('armhf') || (ua.includes('arm') && !ua.includes('arm64'))) {
        arch = 'arm';
    }
    return { os, arch };
}

function triggerDownload(url: string, os: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = os === 'windows' ? 'webip-agent.exe' : 'webip-agent';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

type Props = {
    show: boolean;
    onClose: () => void;
};

const AgentDownloadModal = ({ show, onClose }: Props) => {
    const detected = detectPlatform();
    const [agentType, setAgentType] = useState('mikrotik');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [advancedOS, setAdvancedOS] = useState(detected.os);
    const [advancedArch, setAdvancedArch] = useState(detected.arch);

    const query = useQuery<ReleaseInfo>({
        queryKey: ['agent-release-info', agentType],
        queryFn: async () => {
            const res = await axios.get<ReleaseInfo>('/api/agent/release', {
                params: { type: agentType },
                withCredentials: true,
            });
            return res.data;
        },
        enabled: show,
        staleTime: 60_000,
    });

    const release = query.data;
    const version = release?.version;
    const assets = release?.assets ?? {};
    const detectedKey = `${detected.os}/${detected.arch}`;
    const detectedUrl = assets[detectedKey]?.url ?? null;
    const detectedAvailable = !!detectedUrl;
    const advancedKey = `${advancedOS}/${advancedArch}`;
    const advancedUrl = assets[advancedKey]?.url ?? null;
    const advancedAvailable = !!advancedUrl;
    const advancedSha256 = assets[advancedKey]?.sha256;

    const osOptions = [...new Set(ALL_PLATFORMS.map(p => p.os))].map(os => ({
        id: os,
        label: OS_LABELS[os] ?? os,
    }));

    const archOptions = ALL_PLATFORMS
        .filter(p => p.os === advancedOS)
        .map(p => ({ id: p.arch, label: ARCH_LABELS[p.arch] ?? p.arch }));

    // Reset arch when OS changes and current arch not available for that OS
    const handleOSChange = (val: string | null) => {
        const newOS = val ?? 'linux';
        setAdvancedOS(newOS);
        const available = ALL_PLATFORMS.filter(p => p.os === newOS).map(p => p.arch);
        if (!available.includes(advancedArch)) setAdvancedArch(available[0] ?? 'amd64');
    };

    return (
        <Modal
            show={show}
            title="Download do Agente WebIP"
            onClose={onClose}
            hideControls
            size="md"
        >
            <VBox style={{ gap: '20px', minWidth: '420px' }}>
                {/* Tipo de agente */}
                <CampoSeletor
                    label="Equipamento"
                    value={agentType}
                    onChange={v => setAgentType(v ?? 'mikrotik')}
                    options={AGENT_TYPES}
                    noSearch
                />

                {/* Versão atual */}
                <HBox style={{ alignItems: 'center', gap: '8px' }}>
                    {query.isLoading && <Icon path={mdiLoading} size="18px" spin />}
                    {!query.isLoading && version && (
                        <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                            Versão disponível:{' '}
                            <strong style={{ color: 'var(--tc-color-gray-800)' }}>{version}</strong>
                        </span>
                    )}
                    {!query.isLoading && !version && !query.isError && (
                        <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-500)' }}>
                            Nenhuma versão publicada ainda.
                        </span>
                    )}
                    {query.isError && (
                        <HBox style={{ alignItems: 'center', gap: '4px', color: 'var(--tc-color-danger-600)' }}>
                            <Icon path={mdiAlertCircleOutline} size="16px" />
                            <span style={{ fontSize: '13px' }}>Erro ao carregar informações de versão.</span>
                        </HBox>
                    )}
                </HBox>

                {/* Download rápido */}
                <VBox style={{ gap: '8px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>Download rápido</span>
                    <Botao
                        variant="primary"
                        icon={mdiDownload}
                        disabled={!detectedAvailable || !version}
                        onClick={() => detectedUrl && triggerDownload(detectedUrl, detected.os)}
                    >
                        Baixar para {OS_LABELS[detected.os]} {ARCH_LABELS[detected.arch]}
                    </Botao>
                    {!detectedAvailable && !query.isLoading && version && (
                        <span style={{ fontSize: '12px', color: 'var(--tc-color-warning-600)' }}>
                            Binário não disponível para esta plataforma.
                        </span>
                    )}
                </VBox>

                {/* Separador */}
                <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--tc-color-gray-200)' }} />

                {/* Download avançado (colapsável) */}
                <VBox style={{ gap: '12px' }}>
                    <button
                        onClick={() => setShowAdvanced(v => !v)}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            fontWeight: 600, fontSize: '14px', padding: 0,
                            color: 'var(--tc-color-gray-700)',
                        }}
                    >
                        <Icon path={showAdvanced ? mdiChevronUp : mdiChevronDown} size="18px" />
                        Outros sistemas e arquiteturas
                    </button>

                    {showAdvanced && (
                        <VBox style={{ gap: '12px' }}>
                            <HBox style={{ gap: '12px', alignItems: 'flex-end' }}>
                                <div style={{ flex: 1 }}>
                                    <CampoSeletor
                                        label="Sistema operacional"
                                        value={advancedOS}
                                        onChange={handleOSChange}
                                        options={osOptions}
                                        noSearch
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <CampoSeletor
                                        label="Arquitetura"
                                        value={advancedArch}
                                        onChange={v => setAdvancedArch(v ?? 'amd64')}
                                        options={archOptions}
                                        noSearch
                                    />
                                </div>
                            </HBox>

                            {advancedSha256 && (
                                <VBox style={{ gap: '2px' }}>
                                    <span style={{ fontSize: '11px', color: 'var(--tc-color-gray-500)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        SHA-256: {advancedSha256}
                                    </span>
                                </VBox>
                            )}

                            <Botao
                                variant="outline-primary"
                                icon={mdiDownload}
                                disabled={!advancedAvailable || !version}
                                href={advancedUrl ?? undefined}
                                download={advancedAvailable ? (advancedOS === 'windows' ? 'webip-agent.exe' : 'webip-agent') : undefined}
                            >
                                Baixar {OS_LABELS[advancedOS]} — {ARCH_LABELS[advancedArch] ?? advancedArch}
                            </Botao>

                            {!advancedAvailable && !query.isLoading && version && (
                                <span style={{ fontSize: '12px', color: 'var(--tc-color-warning-600)' }}>
                                    Binário não disponível para {advancedOS}/{advancedArch}.
                                </span>
                            )}

                            {/* Tabela de todos os assets */}
                            {Object.keys(assets).length > 0 && (
                                <VBox style={{ gap: '4px' }}>
                                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)', fontWeight: 600 }}>
                                        Todas as plataformas disponíveis nesta versão:
                                    </span>
                                    {ALL_PLATFORMS.map(({ os, arch }) => {
                                        const key = `${os}/${arch}`;
                                        const url = assets[key]?.url ?? null;
                                        const available = !!url;
                                        return (
                                            <HBox
                                                key={key}
                                                style={{
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    background: available ? 'var(--tc-color-gray-50)' : 'transparent',
                                                    opacity: available ? 1 : 0.4,
                                                    fontSize: '12px',
                                                }}
                                            >
                                                <span>{OS_LABELS[os]} — {ARCH_LABELS[arch] ?? arch}</span>
                                                {available && (
                                                    <button
                                                        onClick={() => triggerDownload(url, os)}
                                                        title={`Baixar ${key}`}
                                                        style={{
                                                            background: 'none', border: 'none', cursor: 'pointer',
                                                            color: 'var(--tc-color-primary-600)', padding: '2px',
                                                            display: 'flex', alignItems: 'center',
                                                        }}
                                                    >
                                                        <Icon path={mdiDownload} size="16px" />
                                                    </button>
                                                )}
                                            </HBox>
                                        );
                                    })}
                                </VBox>
                            )}
                        </VBox>
                    )}
                </VBox>
            </VBox>
        </Modal>
    );
};

export default AgentDownloadModal;
